import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export interface PropertyEnrichment {
  id: string;
  org_id: string;
  latitude: number;
  longitude: number;
  address: string | null;
  est_sqft: number | null;
  est_year_built: number | null;
  est_roof_squares: number | null;
  est_stories: number | null;
  lot_size_sqft: number | null;
  roof_material_guess: string | null;
  owner_name: string | null;
  owner_occupied: boolean | null;
  est_tenure_years: number | null;
  est_home_value: number | null;
  est_equity_bracket: "low" | "medium" | "high" | null;
  mortgage_age_years_est: number | null;
  last_storm_event_date: string | null;
  last_storm_event_type: string | null;
  insurance_carrier_guess: string | null;
  prior_claim_likelihood: "low" | "medium" | "high" | null;
  opportunity_score: number;
  score_reasoning: string | null;
  hot_factors: string[];
  enriched_at: string;
  expires_at: string;
  data_source?: string | null;
  parcel_id?: string | null;
  assessed_value?: number | null;
  owner_mailing_address?: string | null;
}

export function getScoreColor(score: number): string {
  if (score >= 75) return "#10b981"; // green - hot
  if (score >= 50) return "#eab308"; // yellow - warm
  if (score >= 25) return "#f97316"; // orange - cold
  return "#94a3b8"; // grey - skip
}

export function getScoreLabel(score: number): string {
  if (score >= 75) return "🔥 Hot";
  if (score >= 50) return "☀️ Warm";
  if (score >= 25) return "❄️ Cold";
  return "⏭️ Skip";
}

export function useEnrichment() {
  const { orgId } = useAuth();
  const [enrichments, setEnrichments] = useState<PropertyEnrichment[]>([]);
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  const keyFor = (lat: number, lng: number) =>
    `${Math.round(lat * 100000) / 100000},${Math.round(lng * 100000) / 100000}`;

  const fetchExisting = useCallback(async () => {
    if (!orgId) return;
    const { data } = await supabase
      .from("property_enrichments")
      .select("*")
      .eq("org_id", orgId)
      .gt("expires_at", new Date().toISOString())
      .order("opportunity_score", { ascending: false })
      .limit(500);
    if (data) setEnrichments(data as PropertyEnrichment[]);
  }, [orgId]);

  const enrich = useCallback(
    async (latitude: number, longitude: number, address?: string): Promise<PropertyEnrichment | null> => {
      const k = keyFor(latitude, longitude);
      const existing = enrichments.find(e => keyFor(e.latitude, e.longitude) === k);
      if (existing) return existing;

      setLoadingIds(prev => new Set(prev).add(k));
      try {
        const invokePromise = supabase.functions.invoke("enrich-property", {
          body: { latitude, longitude, address },
        });
        const timeoutPromise = new Promise<any>((resolve) =>
          setTimeout(() => resolve({ data: null, error: new Error("Enrichment timed out after 25s") }), 25000)
        );
        const { data, error } = await Promise.race([invokePromise, timeoutPromise]);
        if (error) throw error;
        const enrichment = data?.enrichment as PropertyEnrichment;
        if (enrichment) {
          setEnrichments(prev => {
            const filtered = prev.filter(e => keyFor(e.latitude, e.longitude) !== k);
            return [enrichment, ...filtered];
          });
        }
        return enrichment;
      } catch (err: any) {
        console.error("Enrichment failed:", err);
        toast.error(err?.message?.includes("timed out") ? "Enrichment timed out — tap to retry" : "AI enrichment failed");
        return null;
      } finally {
        setLoadingIds(prev => {
          const next = new Set(prev);
          next.delete(k);
          return next;
        });
      }
    },
    [enrichments]
  );

  const isLoading = (lat: number, lng: number) => loadingIds.has(keyFor(lat, lng));

  return { enrichments, enrich, fetchExisting, isLoading };
}
